import { Component, TemplateRef } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import moment from "moment";
import { MatDialogSizeService } from "src/app/shared/services/mat-dialog-size";
import { LoanDashboardService } from "../../../../loan-dashboard.service";
import { RePaymentService } from "../../re-payment.service";
import {
    CouponType,
    DiscountTypeEnum,
    RepaymentOption,
    RepaymentStatus,
    RewardWalletClaim,
    WalletUsedType,
} from "../../re-payment.interface";
import { catchError, finalize, forkJoin, map, of, Subscription } from "rxjs";
import { UntilDestroy } from "@ngneat/until-destroy";
import { DocumentTypeOfSummary } from "src/app/shared/common.interface";
import { MatRadioChange } from "@angular/material/radio";
import { CouponCongratulationsComponent } from "../../dialogs/coupon-congratulations/coupon-congratulations.component";
import { Location } from "@angular/common";
import { RewardCongratulationsComponent } from "../../dialogs/reward-congratulations/reward-congratulations.component";
import { GlobalService } from "src/app/shared/services/global.service";

@UntilDestroy({ arrayName: "subscriptions" })
@Component({
    selector: "web-emi-summary",
    templateUrl: "./emi-summary.component.html",
    styleUrls: ["./emi-summary.component.scss"],
})
export class EmiSummaryComponent {
    subscriptions: Subscription[] = [];
    applicationId: any;
    repaymentDetails: any;
    selectedEmi: any;
    emiControl = new FormControl();
    repaymentStatus = RepaymentStatus;

    rewardWallet: FormControl = new FormControl("", Validators.required);
    isRewardWalletApplied = false;
    rewardTransactionRefId = "";
    isRewardWalletLoader = false;

    couponControl: FormControl = new FormControl("", Validators.required);
    isCouponApplied = false;
    isValidCouponValid = true;
    isCouponLoader = false;

    isDownloadSummary = false;

    defaultSelectedRepaymentOption = "";
    selectedRepaymentOption = "";
    selectedRepaymentLabel = "";
    repaymentOption = RepaymentOption;

    loanDuration: { [key: string]: any } = {};

    customAmount: FormControl = new FormControl("", [Validators.required]);

    isRewardPointRedeemOn = false;
    maxUsableRedeemBalance = 0;
    isCouponRedeemOn = false;

    discountTypeEnum = DiscountTypeEnum;

    isPaymentLoader = false;

    fullShimmer = false;
    subShimmer = false;

    rewardPointMsg: any;

    onlyForeclose = false;

    constructor(
        public dialog: MatDialog,
        private rePaymentService: RePaymentService,
        public matDialogSizeService: MatDialogSizeService,
        private loanDashboardService: LoanDashboardService,
        private globalService: GlobalService,
        private location: Location
    ) {}

    ngOnInit() {
        const { applicationId, repaymentOption } = history.state || {};

        this.defaultSelectedRepaymentOption = repaymentOption;
        this.selectedRepaymentOption = repaymentOption;

        if (applicationId) {
            this.applicationId = applicationId;
        }

        this.getDetails();
    }

    formatEmiRange(startDate: Date, endDate: Date): string {
        const start = moment(startDate).format("DD MMM").toUpperCase();
        const end = moment(endDate).format("DD MMM").toUpperCase();
        return `${start} - ${end}`;
    }

    onRepaymentTypeChange(option: MatRadioChange) {
        const data = option?.value as any;
        this.selectedRepaymentOption = data?.repaymentOption as string;
        this.selectedRepaymentLabel = data?.label as string;

        if (this.selectedRepaymentOption == this.repaymentOption.EMI) {
            if (data?.label == this.repaymentDetails.dueDelayEmiSum?.label) {
                this.selectedEmi = this.repaymentDetails?.dueDelayEmiSum;
                this.isRewardPointRedeemOn = this.selectedEmi?.isRewardPointRedeemOn || false;
                this.maxUsableRedeemBalance = this.selectedEmi?.maxUsableRedeemBalance || 0;
                this.isCouponRedeemOn = this.selectedEmi?.isCouponRedeemOn || false;
            } else {
                this.selectedEmi = this.repaymentDetails?.emis?.find((emi: any) => emi?.label == data?.label);
                this.isRewardPointRedeemOn = this.repaymentDetails?.isRewardPointRedeemOn || false;
                this.maxUsableRedeemBalance = this.repaymentDetails?.maxUsableRedeemBalance || 0;
                this.isCouponRedeemOn = this.repaymentDetails?.isCouponRedeemOn || false;
            }
        } else if (this.selectedRepaymentOption == this.repaymentOption.FORECLOSE) {
            this.selectedEmi = this.repaymentDetails?.fullPayment;
            this.isRewardPointRedeemOn = this.selectedEmi?.isRewardPointRedeemOn || false;
            this.maxUsableRedeemBalance = this.selectedEmi?.maxUsableRedeemBalance || 0;
            this.isCouponRedeemOn = this.selectedEmi?.isCouponRedeemOn || false;
        } else if (this.selectedRepaymentOption == this.repaymentOption.CUSTOM) {
            this.selectedEmi = {};
        }

        // Discount
        if (
            this.selectedEmi?.interestDiscount != undefined &&
            Object.keys(this.selectedEmi?.interestDiscount).length > 0
        ) {
            const interestDiscount = this.selectedEmi?.interestDiscount;
            if (interestDiscount.discountDetails?.length > 0) {
                interestDiscount?.discountDetails.forEach((element: any) => {
                    if (element?.discountType == DiscountTypeEnum.COUPON_CODE_DISCOUNT) {
                        // this.couponControl.patchValue(element?.code);
                        // this.couponControl.disable();
                        // this.isCouponApplied = true;
                    } else if (element?.discountType == DiscountTypeEnum.REWARD_WALLET) {
                        // this.rewardWallet.patchValue(element?.discount);
                        // this.rewardWallet.disable();
                        // this.isRewardWalletApplied = true;

                        this.rewardTransactionRefId = element?.rewardTransactionRefId?.toString();
                    }
                });

                if (this.hasRemovableDiscount(this.selectedEmi)) {
                    this.removeDiscount();
                }
            }
        }

        this.rewardWallet.setValidators([
            Validators.required,
            Validators.min(0),
            Validators.max(this.maxUsableRedeemBalance ?? 0),
        ]);
        this.rewardWallet.updateValueAndValidity();
    }

    makePayment() {
        let payload: any;

        if (this.selectedRepaymentOption === this.repaymentOption.CUSTOM) {
            payload = {
                applicationId: this.applicationId,
                isForeClose: false,
                amount: this.customAmount.value,
                repaymentOption: this.repaymentOption.CUSTOM,
                ...this.selectedEmi,
                totalPayable: this.customAmount.value,
                label: this.emiControl.value?.label || "",
            };
        } else if (this.selectedRepaymentOption === this.repaymentOption.FORECLOSE) {
            payload = {
                applicationId: this.applicationId,
                isForeClose: true,
                repaymentOption: this.repaymentOption.FORECLOSE,
                ...this.selectedEmi,
                label: this.emiControl.value?.label || "",
            };
        } else if (this.selectedRepaymentOption === this.repaymentOption.EMI) {
            payload = {
                applicationId: this.applicationId,
                isForeClose: false,
                payId: this.selectedEmi?.payIds?.toString() ?? this.selectedEmi?.payId,
                repaymentOption: this.repaymentOption.EMI,
                ...this.selectedEmi,
                label: this.emiControl.value?.label || "",
            };
        }

        if (!payload) return;

        this.isPaymentLoader = true;

        this.rePaymentService
            .repaymentSummary(payload)
            .then((success: boolean) => {
                if (success) {
                    this.isPaymentLoader = false;
                } else {
                    this.isPaymentLoader = false;
                }
            })
            .catch(() => {
                this.isPaymentLoader = false;
            });
    }

    async onBack() {
        if (this.hasRemovableDiscount(this.selectedEmi)) {
            await this.removeDiscount();
        }
        this.location.back();
    }
    hasRemovableDiscount(emi: any): boolean {
        return (emi?.interestDiscount?.discountDetails ?? []).some(
            (d: any) => d?.discountType !== DiscountTypeEnum.WAIVER
        );
    }
    rewardWalletClaim() {
        const payload: RewardWalletClaim = {
            applicationId: this.applicationId?.toString() || "",
            points: this.rewardWallet.value ?? 0,
            type: WalletUsedType.REPAYMENT,
        };

        if (this.rewardWallet.valid) {
            this.isRewardWalletLoader = true;
            this.subscriptions.push(
                this.rePaymentService
                    .rewardWalletClaim(payload)
                    .pipe(
                        map((res: any) => {
                            if (res.success) {
                                this.isRewardWalletLoader = false;
                                this.dialog
                                    .open(RewardCongratulationsComponent, {
                                        width: this.matDialogSizeService.wIII,
                                        disableClose: true,
                                    })
                                    .afterClosed()
                                    .subscribe((res: any) => {
                                        if (res?.data) {
                                            this.getDetails(true);
                                        }
                                    });
                            }
                        }),
                        catchError(() => {
                            this.isRewardWalletLoader = false;
                            return of(null);
                        })
                    )
                    .subscribe()
            );
        }
    }

    removeRewardWalletClaim() {
        if (this.rewardTransactionRefId) {
            this.isRewardWalletLoader = true;

            this.subscriptions.push(
                this.rePaymentService
                    .removeRewardWalletClaim(this.rewardTransactionRefId)
                    .pipe(
                        map(async (res: any) => {
                            this.isRewardWalletLoader = false;
                            if (res.success) {
                                this.rewardWallet.setValue("");
                                this.isRewardWalletApplied = false;
                                this.rewardWallet.enable();
                                this.rewardTransactionRefId = "";

                                this.getDetails(true);
                            }
                        }),
                        catchError(() => {
                            this.isRewardWalletLoader = false;
                            return of(null);
                        })
                    )
                    .subscribe()
            );
        }
    }

    couponRedeem() {
        if (this.couponControl.valid) {
            this.isValidCouponValid = true;

            this.isCouponLoader = true;
            this.subscriptions.push(
                this.rePaymentService
                    .couponRedeem({
                        amount: this.selectedEmi?.totalPayable || 0,
                        code: this.couponControl.value || "",
                        couponType: CouponType.REPAYMENT,
                    })
                    .pipe(
                        map((res: any) => {
                            this.isCouponLoader = false;
                            if (res.success) {
                                this.couponCongratulation({
                                    ...res?.data,
                                    couponCode: this.couponControl.value,
                                });
                            }
                        }),
                        catchError(() => {
                            this.isCouponLoader = false;
                            this.isValidCouponValid = false;
                            return of(null);
                        })
                    )
                    .subscribe()
            );
        }
    }

    couponCongratulation(details: any) {
        this.subscriptions.push(
            this.dialog
                .open(CouponCongratulationsComponent, {
                    width: this.matDialogSizeService.wIIIS,
                    disableClose: true,
                    data: details,
                })
                .afterClosed()
                .pipe(
                    map(async (res: any) => {
                        if (res?.data) {
                            this.getDetails(true);
                        }
                    })
                )
                .subscribe()
        );
    }

    resetAppliedCouponCode() {
        this.isCouponLoader = true;
        this.subscriptions.push(
            this.rePaymentService
                .removeCoupon()
                .pipe(
                    map(async (res: any) => {
                        if (res.success) {
                            this.isCouponLoader = false;

                            this.couponControl.setValue("");
                            this.isCouponApplied = false;
                            this.couponControl.enable();

                            this.getDetails(true);
                        }
                    }),
                    catchError(() => {
                        this.isCouponLoader = false;
                        this.isValidCouponValid = false;
                        return of(null);
                    })
                )
                .subscribe()
        );
    }

    getDetails(subShimmer = false): Promise<any> {
        return new Promise((resolve, reject) => {
            subShimmer ? (this.subShimmer = true) : (this.fullShimmer = true);

            const sub = this.loanDashboardService
                .getDashboardDetails$(false, false)
                .pipe(
                    finalize(() => {
                        this.subShimmer = false;
                        this.fullShimmer = false;
                    })
                )
                .subscribe({
                    next: async (res: any) => {
                        try {
                            if (res?.data) {
                                this.repaymentDetails = res.data;
                                this.rewardPointMsg = { ...res.rewardPoint };

                                this.onlyForeclose = this.repaymentDetails?.emis?.every((emi: any) => !emi?.isPayable);

                                // ✅ wait for async function
                                await this.createLoanDurationObj();

                                this.customAmount.setValidators([
                                    Validators.required,
                                    Validators.min(this.repaymentDetails?.minCustomAmount ?? 0),
                                    Validators.max(this.repaymentDetails?.maxCustomAmount ?? 0),
                                ]);

                                this.customAmount.updateValueAndValidity({ emitEvent: false });
                            }

                            resolve(res); // ✅ success
                        } catch (err) {
                            reject(err); // ❌ error in processing
                        }
                    },
                    error: (err) => {
                        reject(err); // ❌ API error
                    },
                });

            // optional: track subscription if you still need
            this.subscriptions.push(sub);
        });
    }

    downloadSummary() {
        this.isDownloadSummary = true;

        this.globalService
            .downloadSummary(
                DocumentTypeOfSummary.EMI_REPAYMENT_SCHEDULE,
                "EMI_REPAYMENT_SUMMARY",
                "",
                this.applicationId?.toString(),
                this.repaymentDetails?.loanId ?? ""
            )
            .then(() => {
                this.isDownloadSummary = false;
            })
            .finally(() => {
                this.isDownloadSummary = false;
            });
    }

    createLoanDurationObj(): Promise<void> {
        return new Promise((resolve) => {
            if (this.repaymentDetails && Object.keys(this.repaymentDetails).length) {
                let durationObj: {
                    [key: string]: {
                        label: string;
                        displayLabel: string;
                        value: string;
                        repaymentOption: string;
                        order: number;
                    };
                } = {};

                // EMI
                if (
                    this.repaymentDetails?.emis &&
                    this.repaymentDetails?.emis?.length &&
                    this.defaultSelectedRepaymentOption != this.repaymentOption.FORECLOSE
                ) {
                    this.repaymentDetails.emis.forEach((emi: any) => {
                        const length = Object.keys(durationObj)?.length;
                        if (emi?.isPayable) {
                            durationObj[`EMI_${length}`] = {
                                label: emi?.label,
                                displayLabel: "Pay" + " " + emi?.label,
                                value: length.toString(),
                                repaymentOption: RepaymentOption.EMI,
                                order: length + 1,
                            };
                        }
                    });
                }

                // DUE
                if (
                    this.repaymentDetails?.dueDelayEmiSum &&
                    Object.keys(this.repaymentDetails?.dueDelayEmiSum).length &&
                    this.defaultSelectedRepaymentOption != this.repaymentOption.FORECLOSE
                ) {
                    const length = Object.keys(durationObj)?.length;
                    durationObj["MULTIPLE_EMI"] = {
                        label: this.repaymentDetails.dueDelayEmiSum?.label,
                        displayLabel: "Pay" + " " + this.repaymentDetails.dueDelayEmiSum?.label,
                        value: length?.toString(),
                        repaymentOption: RepaymentOption.EMI,
                        order: length + 1,
                    };
                }

                // CUSTOM
                if (this.repaymentDetails?.isCustomAmountApplicable) {
                    const length = Object.keys(durationObj)?.length;
                    durationObj["CUSTOM_AMOUNT"] = {
                        label: "Other/Custom Amount",
                        displayLabel: "Other/Custom Amount",
                        value: length?.toString(),
                        repaymentOption: RepaymentOption.CUSTOM,
                        order: length + 1,
                    };
                }

                // FORECLOSE
                if (
                    this.repaymentDetails?.isForeClose &&
                    this.repaymentDetails?.fullPayment &&
                    Object.keys(this.repaymentDetails?.fullPayment).length
                ) {
                    const length = Object.keys(durationObj)?.length;
                    durationObj["FORECLOSE"] = {
                        label: "Full Loan Closure",
                        displayLabel: "Full Loan Closure",
                        value: length?.toString(),
                        repaymentOption: RepaymentOption.FORECLOSE,
                        order: length + 1,
                    };
                }

                this.loanDuration = durationObj;

                // Set default selected EMI
                if (this.selectedRepaymentOption == this.repaymentOption.EMI) {
                    const emiDetails = Object.values(this.loanDuration)[0];

                    if (this.selectedRepaymentLabel == this.repaymentDetails.dueDelayEmiSum?.label) {
                        this.emiControl.patchValue(this.loanDuration["MULTIPLE_EMI"]);
                        this.selectedEmi = this.repaymentDetails?.dueDelayEmiSum;
                        this.isRewardPointRedeemOn = this.selectedEmi?.isRewardPointRedeemOn || false;
                        this.maxUsableRedeemBalance = this.selectedEmi?.maxUsableRedeemBalance || 0;
                        this.isCouponRedeemOn = this.selectedEmi?.isCouponRedeemOn || false;
                    } else {
                        this.emiControl.patchValue(emiDetails);
                        this.selectedEmi = this.repaymentDetails?.emis?.find(
                            (emi: any) => emi?.label == emiDetails?.label
                        );
                        this.isRewardPointRedeemOn = this.repaymentDetails?.isRewardPointRedeemOn || false;
                        this.maxUsableRedeemBalance = this.repaymentDetails?.maxUsableRedeemBalance || 0;
                        this.isCouponRedeemOn = this.repaymentDetails?.isCouponRedeemOn || false;
                    }
                } else if (this.selectedRepaymentOption == this.repaymentOption.FORECLOSE) {
                    this.emiControl.patchValue(this.loanDuration["FORECLOSE"]);
                    this.selectedEmi = this.repaymentDetails?.fullPayment;
                    this.isRewardPointRedeemOn = this.selectedEmi?.isRewardPointRedeemOn || false;
                    this.maxUsableRedeemBalance = this.selectedEmi?.maxUsableRedeemBalance || 0;
                    this.isCouponRedeemOn = this.selectedEmi?.isCouponRedeemOn || false;
                }

                this.rewardWallet.setValidators([
                    Validators.required,
                    Validators.min(0),
                    Validators.max(this.maxUsableRedeemBalance ?? 0),
                ]);
                this.rewardWallet.updateValueAndValidity();

                // Discount
                if (
                    this.selectedEmi?.interestDiscount != undefined &&
                    Object.keys(this.selectedEmi?.interestDiscount).length > 0
                ) {
                    const interestDiscount = this.selectedEmi?.interestDiscount;
                    if (interestDiscount.discountDetails?.length > 0) {
                        interestDiscount?.discountDetails.forEach((element: any) => {
                            if (element?.discountType == DiscountTypeEnum.COUPON_CODE_DISCOUNT) {
                                this.couponControl.patchValue(element?.code);
                                this.couponControl.disable();
                                this.isCouponApplied = true;
                            } else if (element?.discountType == DiscountTypeEnum.REWARD_WALLET) {
                                this.rewardWallet.patchValue(element?.discount);
                                this.rewardWallet.disable();
                                this.isRewardWalletApplied = true;
                                this.rewardTransactionRefId = element?.rewardTransactionRefId?.toString();
                            }
                        });
                    }
                }
            }
            resolve();
        });
    }

    openCalculationDialog(templateRef: TemplateRef<any>, width?: string) {
        this.dialog.open(templateRef, {
            width: width || "auto",
            disableClose: false,
        });
    }

    hasMultiplePenaltyCharges = () =>
        (this.repaymentDetails?.emis ?? []).filter((e: any) => e?.penalInterest > 0).length > 1;
    hasMultipleEcsCharges = () => (this.repaymentDetails?.emis ?? []).filter((e: any) => e?.ecsCharge > 0).length > 1;

    getDiscountTotal() {
        const discountDetails = this.selectedEmi?.interestDiscount?.discountDetails ?? [];

        const discountTotal = discountDetails.reduce((sum: number, d: any) => {
            return d?.discount > 0 ? sum + d.discount : sum;
        }, 0);

        const foreCloseDiscount = this.selectedEmi?.foreCloseDiscount ?? 0;

        return discountTotal + foreCloseDiscount;
    }

    async removeDiscount(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.subShimmer = true;
            const calls = [];

            // reward API
            if (this.rewardTransactionRefId) {
                calls.push(this.rePaymentService.removeRewardWalletClaim(this.rewardTransactionRefId));
            }

            // coupon API
            calls.push(this.rePaymentService.removeCoupon());

            forkJoin(calls).subscribe({
                next: async (responses: any[]) => {
                    // UI updates
                    this.couponControl.patchValue("");
                    this.couponControl.enable();
                    this.isCouponApplied = false;

                    this.rewardWallet.patchValue("");
                    this.rewardWallet.enable();
                    this.isRewardWalletApplied = false;

                    this.rewardTransactionRefId =
                        this.selectedEmi?.interestDiscount?.discountDetails?.rewardTransactionRefId?.toString();

                    this.subShimmer = false;

                    await this.getDetails(true);

                    resolve(responses); // ✅ success
                },
                error: (err) => {
                    this.subShimmer = false;
                    reject(err);
                },
            });
        });
    }

    blockInvalidInput(event: KeyboardEvent | ClipboardEvent) {
        const invalidKeys = ["e", "E", "+", "-", "."];

        // Case 1: keydown
        if ("key" in event) {
            if (invalidKeys.includes(event.key)) {
                event.preventDefault();
            }
            return;
        }

        // Case 2: paste
        if ("clipboardData" in event) {
            // If you want to completely block paste:
            event.preventDefault();

            // Or, if you only want to block when pasted text has invalid chars:
            // const pasted = event.clipboardData?.getData('text') ?? '';
            // if (!/^[0-9]*$/.test(pasted)) {
            //   event.preventDefault();
            // }
        }
    }
}
